<script setup lang="ts">
import IconLogo from "@/assets/icons/IconLogo.vue";
import IconDatabase from "@/assets/icons/IconDatabase.vue";
import IconGrid from "@/assets/icons/IconGrid.vue";
import IconClock from "@/assets/icons/IconClock.vue";
import IconPlus from "@/assets/icons/IconPlus.vue";
import IconUser from "@/assets/icons/IconUser.vue";
import {useRouter} from "vue-router";
import {computed, type Component, ref} from "vue";
import FilesUploadModal from "@/components/modules/FileUploadModal/FilesUploadModal.vue";
import {exit} from "@/composables/auth";
import IconLogout from "@/assets/icons/IconLogout.vue";

// Интерфейс для элементов меню навигации
interface IMenuRouteItem {
  path: string;
  name: string;
  tooltip: string;
  icon: Component;
}

const router = useRouter();

// Активный роут.
const activeRouterPath = computed(() => router.currentRoute.value.fullPath);

// Список навигации
const menuRouterList: IMenuRouteItem[] = [
  {
    path: "map-view",
    name: "map-view",
    tooltip: "Карта",
    icon: IconGrid
  },
  {
    path: "event-log",
    name: "event-log",
    tooltip: "Журнал событий",
    icon: IconClock
  },
  {
    path: "database",
    name: "database",
    tooltip: "База данных",
    icon: IconDatabase
  }
];

// Переключение пунктов меню.
const changeRouteHandler = async (path: string) => {
  await router.push(path);
};

// Отображение модального окна загрузки файлов
const isShowFileUpload = ref(false);
// Переключение отображения модального окна загрузки файлов
const toggleShowFileUpload = () => {
  isShowFileUpload.value = !isShowFileUpload.value;
};

const handleShowFileUpload = () => {
  if (isShowFileUpload.value) {
    isShowUnsavedChangesModal.value = true;
  } else {
    isShowFileUpload.value = true;
  }
};

// Отображение модального окна подтверждения сохранения изменений
const isShowUnsavedChangesModal = ref(false);
const showUnsavedChangesModal = () => {
  isShowUnsavedChangesModal.value = true;
};

const hideUnsavedChangesModal = () => {
  isShowUnsavedChangesModal.value = false;
};

const handleShowUnsavedChangesModal = () => {
  isShowUnsavedChangesModal.value = false;
  isShowFileUpload.value = false;
};

// Модальное окно выхода из системы
const isShowLogoutModal = ref(false);
const showLogoutModal = () => {
  isShowLogoutModal.value = true;
};
const hideLogoutModal = () => {
  isShowLogoutModal.value = false;
};

// Выход из системы.
const doLogout = async () => {
  await exit();
};

// Меню при клике на пользователя
const isShowUserMenu = ref(false);

const toggleUserMenu = () => {
  isShowUserMenu.value = !isShowUserMenu.value;
};

const closeUserMenu = () => {
  isShowUserMenu.value = false;
};
</script>

<template>
  <aside class="sidebar">
    <div class="sidebar__logo">
      <IconLogo />
    </div>

    <nav class="sidebar__nav">
      <button
        v-for="menuItem in menuRouterList"
        :key="menuItem.name"
        v-tooltip:right:16="menuItem.tooltip"
        class="sidebar__nav-button"
        :class="{'sidebar__nav-button--active': activeRouterPath === '/' + menuItem.path}"
        @click="changeRouteHandler(menuItem.path)"
      >
        <Component :is="menuItem.icon" />
      </button>
    </nav>

    <div class="sidebar__bottom">
      <ButtonIconAccentPrimary
        v-tooltip:right:17="'Загрузить данные'"
        class="sidebar__action-button"
        @click="handleShowFileUpload"
      >
        <IconPlus />
      </ButtonIconAccentPrimary>

      <div
        v-click-outside="closeUserMenu"
        class="sidebar__user-wrapper"
      >
        <button
          class="sidebar__user-button"
          @click="toggleUserMenu"
        >
          <IconUser />
        </button>
        <ul
          v-if="isShowUserMenu"
          class="sidebar__user-list"
        >
          <li
            class="sidebar__user-item"
            @click="showLogoutModal"
          >
            <IconLogout /> Выход
          </li>
        </ul>
      </div>
    </div>
    <!--  Модальное окно выхода из системыы   -->
    <Modal
      v-if="isShowLogoutModal"
      confirm-text="Выйти"
      cancel-text="Отмена"
      title="Выход из аккаунта"
      teleport="#app"
      @confirm="doLogout"
      @cancel="hideLogoutModal"
    >
      Уверены, что хотите выйти?
    </Modal>
    <!--  Модальное окно загрузки файлов  -->
    <FilesUploadModal
      v-if="isShowFileUpload"
      @toggle-show="toggleShowFileUpload"
      @show-unsaved-changes="showUnsavedChangesModal"
    />
    <!--  Модальное окно подтверждения изменений окна загрузки файлов.  -->
    <Modal
      v-if="isShowUnsavedChangesModal && isShowFileUpload"
      teleport="#app"
      title="Данные не сохранились"
      confirm-text="Закрыть"
      @cancel="hideUnsavedChangesModal"
      @confirm="handleShowUnsavedChangesModal"
    >
      Данные не загружены. Уверены, что хотите закрыть окно загрузки?
    </Modal>
    <div
      v-if="isShowFileUpload"
      class="block-view"
    />
  </aside>
</template>

<style scoped lang="scss">
.block-view {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  background-color: rgba(0,0,0,0.2);
}

.sidebar {
  display: flex;
  flex-direction: column;
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  padding: 30px 10px;
  background-color: var(--background-bg-white);
  gap: 40px;
  z-index: 11;

  &__logo {
    display: flex;
    justify-content: center;
  }

  &__nav {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  &__nav-button {
    cursor: pointer;
    padding: 10px;
    color: var(--blue-blue60);
    border-radius: 8px;
    border: none;
    background-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s;

    &:hover:not(&--active) {
      background-color: var(--background-bg-hover);
    }

    &--active {
      background-color: var(--background-bg-active-hover);
    }
  }

  &__bottom {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    margin-top: auto;
  }

  &__user-wrapper {
    position: relative;
  }

  &__user-button {
    cursor: pointer;
    border: none;
    background-color: transparent;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__user-list {
    position: absolute;
    background-color: var(--background-bg-white);
    box-shadow: 1px 2px 10px 0 var(--gray-gray50);
    padding: 4px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    left: calc(100% + 17px);
    top: 0;
  }

  &__user-item {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: var(--text-text-main);
    display: flex;
    gap: 6px;
    align-items: center;
    border-radius: 8px;
    padding: 6px 10px;
    background-color: var(--background-bg-white);
    cursor: pointer;
    user-select: none;

    &:hover {
      background-color: var(--background-bg-hover);
    }
  }
}
</style>